/**
 * An object that defines the display modes for editing or viewing event rules.
 *
 * Properties:
 * - `EDIT`: Represents the mode where the event rule can be edited.
 * - `VIEW`: Represents the mode where the event rule is displayed in a readonly state.
 *
 * This constant helps in toggling between edit and view states of an event rule in the application.
 */
export const EDIT_EVENT_RULE_DISPLAY_MODE = {
    EDIT: 'edit',
    VIEW: 'view',
} as const;

export type EditEventRuleDisplayMode =
    (typeof EDIT_EVENT_RULE_DISPLAY_MODE)[keyof typeof EDIT_EVENT_RULE_DISPLAY_MODE];

/**
 * Represents the various possible statuses for an event's availability or sale.
 * This object defines constant string values that can be used to describe
 * the current state of the event within the system.
 *
 * Properties:
 * - FOR_SALE: Represents the event being available for sale.
 * - NOT_FOR_SALE: Indicates that the event is opted out of being sold.
 * - BLACKOUT: Specifies that the event is in a blackout period, meaning
 *   it is unavailable for sale during this time.
 * - SALES_ENDED: Represents a status where sales for the event have been stopped.
 * - FOR_SALE_OUTSIDE_HOURS: Indicates the event is for sale but outside normal business hours (HOO).
 * - OUTSIDE_HOURS: Specifies that the event is outside of standard business hours (HOO).
 * - SOLD_OUT: Indicates that all reservations for the event have been sold out.
 */
export const EVENT_STATUS = {
    FOR_SALE: 'FOR_SALE',
    NOT_FOR_SALE: 'NOT_FOR_SALE',
    BLACKOUT: 'BLACKOUT',
    SALES_ENDED: 'SALES_ENDED',
    FOR_SALE_OUTSIDE_HOURS: 'FOR_SALE_OUTSIDE_HOURS',
    OUTSIDE_HOURS: 'OUTSIDE_HOURS',
    SOLD_OUT: 'SOLD_OUT',
    BLACKOUT_LOCKED: 'BLACKOUT_LOCKED',
} as const;

/**
 * Represents the mapping of event statuses to their respective badge labels and associated values.
 *
 * Each key in the object corresponds to a specific type of event status badge, holding
 * information about its display label and the status values it maps to, as defined in EVENT_STATUS.
 *
 * Properties:
 * - FOR_SALE: Indicates that an event is for sale, with associated statuses for sale and for sale outside hours.
 * - NOT_FOR_SALE: Indicates that an event is not for sale, with associated opt-out status.
 * - BLACKOUT: Indicates a blackout period with the associated blackout status.
 * - SALES_ENDED: Indicates sales have ended for the event with the associated sales-stopped status.
 * - SOLD_OUT: Represents a sold-out state for the event with the associated sold-out status.
 * - OUTSIDE_HOURS: Indicates the event is outside of operating hours with the associated outside hours status.
 */
export const EVENT_STATUS_BADGE = {
    FOR_SALE: {
        label: 'For Sale',
        id: 'FOR_SALE',
        value: [EVENT_STATUS.FOR_SALE, EVENT_STATUS.FOR_SALE_OUTSIDE_HOURS],
        description:
            'Spots are available for drivers to reserve at the listed event rate on SpotHero.',
        color: 'green.100',
    },
    NOT_FOR_SALE: {
        label: 'Not For Sale',
        id: 'NOT_FOR_SALE',
        value: [EVENT_STATUS.NOT_FOR_SALE],
        description:
            'Spots are not available for drivers to reserve at the event rate. This happens when you choose to stop selling parking for a specific event.  Note: Event statuses can be updated at any time. You must honor any existing reservations made before sales were stopped.',
        color: 'red.100',
    },
    BLACKOUT: {
        label: 'Blackout',
        id: 'BLACKOUT',
        value: [EVENT_STATUS.BLACKOUT, EVENT_STATUS.BLACKOUT_LOCKED],
        description:
            'Spots are not available due to a blackout.  Note:  You must honor any existing reservations made before the blackout was created.',
        color: 'red.100',
    },
    SALES_ENDED: {
        label: 'Sales Ended',
        id: 'SALES_ENDED',
        value: [EVENT_STATUS.SALES_ENDED],
        description: '',
        color: 'red.100',
    },
    SOLD_OUT: {
        label: 'Sold Out',
        id: 'SOLD_OUT',
        value: [EVENT_STATUS.SOLD_OUT],
        description:
            'All allotted inventory has been reserved.  Tip: To continue selling, add more inventory for the event.',
        color: 'yellow.100',
    },
    OUTSIDE_HOURS: {
        label: 'Outside Hours',
        id: 'OUTSIDE_HOURS',
        value: [EVENT_STATUS.OUTSIDE_HOURS],
        description:
            'Spots are not available because the event’s start or end times fall outside your facility’s bookable hours of operation. Contact  sellersupport@spothero.com if you want to enable sales for this event.',
        color: 'gray.100',
    },
} as const;
